import React, { useState, useEffect, useCallback, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const buildFetchOptions = (options = {}) => {
  const adminToken = localStorage.getItem('admin_jwt');
  const headers = options.headers || {};
  if (adminToken && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${adminToken}`;
  }
  return { ...options, headers, credentials: 'include' };
};

const STATUS_STYLES = {
  generating: { background: '#4a3800', color: '#fbbf24', border: '1px solid #92400e' },
  ready:      { background: '#1e3a5f', color: '#60a5fa', border: '1px solid #2563eb' },
  approved:   { background: '#064e3b', color: '#34d399', border: '1px solid #059669' },
  posted:     { background: '#1a2e1a', color: '#86efac', border: '1px solid #22c55e' },
  expired:    { background: '#2a2a2a', color: '#888', border: '1px solid #555', textDecoration: 'line-through' },
  error:      { background: '#3b1111', color: '#f87171', border: '1px solid #dc2626' },
  draft:      { background: '#333', color: '#aaa', border: '1px solid #555' },
};

const CHARACTER_DISPLAY = {
  falstaff: 'Falstaff',
  sir_andrew: 'Sir Andrew',
  iago: 'Iago',
  lady_macbeth: 'Lady Macbeth',
  miranda: 'Miranda',
  bitbard: 'BitBard',
};

const MEDIA_SCHEDULE = {
  0: 'pendant', 1: 'pendant', 2: 'video',
  3: 'pendant', 4: 'video', 5: 'pendant', 6: 'video',
};

/** Calendar-local YYYY-MM-DD — avoids UTC off-by-one from toISOString().split('T')[0] */
function localDateString(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const DramaturgWorkshop = ({ showToast }) => {
  const [concept, setConcept] = useState('');
  const [pinLine, setPinLine] = useState('');
  const [generating, setGenerating] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [variants, setVariants] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [mediaFormat, setMediaFormat] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return MEDIA_SCHEDULE[tomorrow.getDay() === 0 ? 6 : tomorrow.getDay() - 1] || 'pendant';
  });
  const [scheduledDate, setScheduledDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return localDateString(tomorrow);
  });
  const [queue, setQueue] = useState([]);
  const [error, setError] = useState(null);
  const [approving, setApproving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const pollRef = useRef(null);

  // Fetch queue on mount
  const fetchQueue = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/dramaturg/queue`, buildFetchOptions());
      if (resp.ok) {
        const data = await resp.json();
        setQueue(data.items || []);
      }
    } catch (err) {
      console.error('Failed to fetch dramaturg queue:', err);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Poll for generation status — ref-based to avoid stale closures
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!activeSession || !generating) return;

    const poll = async () => {
      try {
        const resp = await fetch(
          `${BACKEND_URL}/api/admin/dramaturg/status/${activeSession}`,
          buildFetchOptions()
        );
        if (resp.ok) {
          const data = await resp.json();
          setPollCount(c => c + 1);
          if (data.status === 'ready' && data.variants) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setVariants(data.variants);
            setGenerating(false);
            setError(null);
            fetchQueue();
            if (showToast) showToast('Variants ready for review');
          } else if (data.status === 'error') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setGenerating(false);
            setError(data.error || 'Generation failed');
            fetchQueue();
          }
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    };

    // Poll immediately, then every 4 seconds
    poll();
    pollRef.current = setInterval(poll, 4000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [activeSession, generating, fetchQueue, showToast]);

  const handleGenerate = async () => {
    if (!concept.trim()) return;
    
    // Clear all previous state for fresh iteration
    setGenerating(true);
    setVariants(null);
    setSelectedVariant(null);
    setActiveSession(null);
    setError(null);
    setPollCount(0);
    
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/dramaturg/generate`, buildFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          concept: concept.trim(),
          pin_line: pinLine.trim() || null,
        }),
      }));
      if (resp.ok) {
        const data = await resp.json();
        setActiveSession(data.session_id);
        fetchQueue();
      } else {
        const err = await resp.json().catch(() => ({}));
        setError(err.detail || 'Failed to start generation');
        setGenerating(false);
      }
    } catch (err) {
      setError(err.message);
      setGenerating(false);
    }
  };

  const handleApprove = async () => {
    if (selectedVariant === null || !activeSession) return;
    setApproving(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/dramaturg/approve`, buildFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: activeSession,
          variant_idx: selectedVariant,
          media_format: mediaFormat,
          scheduled_date: scheduledDate,
        }),
      }));
      if (resp.ok) {
        // Success: UI state change is sufficient feedback
        setVariants(null);
        setSelectedVariant(null);
        setActiveSession(null);
        setConcept('');
        setPinLine('');
        fetchQueue();
      } else {
        const err = await resp.json().catch(() => ({}));
        setError(err.detail || 'Approval failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setApproving(false);
    }
  };

  const handlePostNow = async () => {
    if (selectedVariant === null || !activeSession) return;
    setPosting(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/dramaturg/post-now`, buildFetchOptions({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: activeSession,
          variant_idx: selectedVariant,
          media_format: mediaFormat,
        }),
      }));
      if (resp.ok) {
        // Success: UI state change is sufficient feedback
        setVariants(null);
        setSelectedVariant(null);
        setActiveSession(null);
        setConcept('');
        setPinLine('');
        fetchQueue();
      } else {
        const err = await resp.json().catch(() => ({}));
        setError(err.detail || 'Post failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setPosting(false);
    }
  };

  const handleClearAll = () => {
    // Throw everything away and start fresh
    setVariants(null);
    setSelectedVariant(null);
    setActiveSession(null);
    setError(null);
    setGenerating(false);
    setPosting(false);
    setApproving(false);
    setPollCount(0);
    // Success: UI state change is sufficient feedback
  };

  const handleDelete = async (id) => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/dramaturg/${id}`, buildFetchOptions({
        method: 'DELETE',
      }));
      if (resp.ok) {
        // Clear variant review if we just deleted the active session
        if (activeSession === id) {
          setActiveSession(null);
          setVariants(null);
          setSelectedVariant(null);
          setGenerating(false);
          setPollCount(0);
          setError(null);
        }
        fetchQueue();
        // Success: list re-fetch is sufficient feedback
      }
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleReopen = async (item) => {
    if (item.status !== 'ready' || !item.has_variants) return;
    // Fetch full variant data
    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/admin/dramaturg/status/${item.id}`,
        buildFetchOptions()
      );
      if (resp.ok) {
        const data = await resp.json();
        if (data.variants) {
          setActiveSession(item.id);
          setVariants(data.variants);
          setConcept(item.concept_text);
          setSelectedVariant(null);
          setGenerating(false);
        }
      }
    } catch (err) {
      console.error('Reopen failed:', err);
    }
  };

  return (
    <div style={{ padding: '0 16px', maxWidth: 900 }}>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: '16px 0 4px', color: '#e2e8f0', fontSize: 18 }}>
          Dramaturg Workshop
        </h3>
        <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>
          Author a scene concept with an optional pin line. The troupe generates 3 variants with different casts.
          Post immediately or schedule for 8am.
        </div>
      </div>

      {/* --- Concept Input --- */}
      <div style={{
        background: '#1a1a2e', border: '1px solid #333', borderRadius: 8,
        padding: 16, marginBottom: 20,
      }}>
        <label style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>
          SCENE CONCEPT
        </label>
        <textarea
          value={concept}
          onChange={(e) => setConcept(e.target.value)}
          placeholder="King Tramp has a discoloration on his hand which has sparked controversy and wild speculation in the court."
          style={{
            width: '100%', minHeight: 80, background: '#111', border: '1px solid #444',
            borderRadius: 6, color: '#e2e8f0', padding: '10px 12px', fontSize: 14,
            fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
          }}
          disabled={generating}
        />
        
        <label style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, display: 'block', marginTop: 12, marginBottom: 6 }}>
          PIN LINE (OPTIONAL)
        </label>
        <textarea
          value={pinLine}
          onChange={(e) => setPinLine(e.target.value)}
          placeholder='King Tramp: "They say my hand is painted, but I call it strategic tanning."'
          style={{
            width: '100%', minHeight: 60, background: '#111', border: '1px solid #444',
            borderRadius: 6, color: '#e2e8f0', padding: '10px 12px', fontSize: 14,
            fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
          }}
          disabled={generating}
        />
        <div style={{ color: '#64748b', fontSize: 11, marginTop: 4, fontStyle: 'italic' }}>
          A specific line that must appear verbatim. Format: "Speaker: Line" — can be troupe member (Iago, Andy, LM) or guest (King Tramp, Duke Whoever)
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
          <button
            onClick={handleGenerate}
            disabled={generating || !concept.trim()}
            style={{
              background: generating ? '#333' : '#2563eb', color: '#fff',
              border: 'none', borderRadius: 6, padding: '8px 20px', fontSize: 14,
              cursor: generating ? 'wait' : 'pointer', fontWeight: 600,
            }}
          >
            {generating ? 'Generating...' : 'Generate Variants'}
          </button>
          {variants && !generating && (
            <button
              onClick={handleClearAll}
              style={{
                background: '#dc2626', color: '#fff',
                border: 'none', borderRadius: 6, padding: '8px 20px', fontSize: 14,
                cursor: 'pointer', fontWeight: 600,
              }}
            >
              Discard & Start Fresh
            </button>
          )}
          {generating && (
            <span style={{ color: '#fbbf24', fontSize: 13 }}>
              Generating 3 variants (~60-90s)... {pollCount > 0 ? `(checked ${pollCount}x)` : ''}
            </span>
          )}
        </div>
        {error && (
          <div style={{ 
            background: '#3b1111', border: '1px solid #dc2626', borderRadius: 6,
            padding: '12px', marginTop: 8, display: 'flex', alignItems: 'center', 
            justifyContent: 'space-between',
          }}>
            <span style={{ color: '#f87171', fontSize: 13 }}>
              Error: {error}
            </span>
            <button
              onClick={handleClearAll}
              style={{
                background: '#dc2626', color: '#fff', border: 'none',
                borderRadius: 4, padding: '4px 12px', fontSize: 12,
                cursor: 'pointer', fontWeight: 600,
              }}
            >
              Clear & Retry
            </button>
          </div>
        )}
      </div>

      {/* --- Variant Review --- */}
      {variants && variants.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ color: '#e2e8f0', margin: '0 0 12px', fontSize: 15 }}>
            Review Variants ({variants.length})
          </h4>
          {variants.map((v, idx) => (
            <div
              key={idx}
              onClick={() => setSelectedVariant(idx)}
              style={{
                background: selectedVariant === idx ? '#0f2a1f' : '#1a1a2e',
                border: selectedVariant === idx ? '2px solid #22c55e' : '1px solid #333',
                borderRadius: 8, padding: 16, marginBottom: 12, cursor: 'pointer',
                transition: 'border-color 0.2s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="radio"
                    checked={selectedVariant === idx}
                    onChange={() => setSelectedVariant(idx)}
                    style={{ accentColor: '#22c55e' }}
                  />
                  <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 600 }}>
                    VARIANT {idx + 1}
                  </span>
                  <span style={{ color: '#60a5fa', fontSize: 13 }}>
                    {v.cast.map(c => CHARACTER_DISPLAY[c] || c).join(' / ')}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{
                    background: '#1e3a5f', color: '#60a5fa', padding: '2px 8px',
                    borderRadius: 4, fontSize: 12, fontWeight: 700,
                  }}>
                    {v.total_score}/30
                  </span>
                  {v.scores && (
                    <>
                      <span style={{ color: '#666', fontSize: 11 }} title="Comedy">
                        C:{v.scores.comedy}
                      </span>
                      <span style={{ color: '#666', fontSize: 11 }} title="Arc">
                        A:{v.scores.arc}
                      </span>
                      <span style={{ color: '#666', fontSize: 11 }} title="Voice">
                        V:{v.scores.voice}
                      </span>
                      <span style={{ color: '#666', fontSize: 11 }} title="Momentum">
                        M:{v.scores.momentum}
                      </span>
                      <span style={{ color: '#666', fontSize: 11 }} title="Specificity">
                        S:{v.scores.specificity}
                      </span>
                      <span style={{ color: '#666', fontSize: 11 }} title="Commitment">
                        K:{v.scores.commitment}
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>
                <span style={{ color: '#555', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Visual brief (feeds image/video, not posted)
                </span>
                <div style={{ fontStyle: 'italic', marginTop: 2 }}>
                  {v.incipit?.substring(0, 200)}{v.incipit?.length > 200 ? '...' : ''}
                </div>
              </div>

              {v.turns && v.turns.map((turn, ti) => (
                <div key={ti} style={{ marginBottom: 6, paddingLeft: 8, borderLeft: '2px solid #333' }}>
                  <span style={{
                    color: turn.character === 'bitbard' ? '#fbbf24' : '#60a5fa',
                    fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
                  }}>
                    {CHARACTER_DISPLAY[turn.character] || turn.character}
                  </span>
                  <div style={{ color: '#cbd5e1', fontSize: 13, marginTop: 2 }}>
                    {turn.text}
                  </div>
                </div>
              ))}
            </div>
          ))}

          {/* Approval controls */}
          <div style={{
            background: '#1a1a2e', border: '1px solid #333', borderRadius: 8,
            padding: 16, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center',
          }}>
            <div>
              <label style={{ color: '#94a3b8', fontSize: 11, display: 'block', marginBottom: 4 }}>FORMAT</label>
              <select
                value={mediaFormat}
                onChange={(e) => setMediaFormat(e.target.value)}
                style={{
                  background: '#111', color: '#e2e8f0', border: '1px solid #444',
                  borderRadius: 4, padding: '6px 10px', fontSize: 13,
                }}
              >
                <option value="pendant">Pendant (image)</option>
                <option value="video">Video (4s reel)</option>
              </select>
            </div>
            <div>
              <label style={{ color: '#94a3b8', fontSize: 11, display: 'block', marginBottom: 4 }}>SCHEDULED DATE</label>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                style={{
                  background: '#111', color: '#e2e8f0', border: '1px solid #444',
                  borderRadius: 4, padding: '6px 10px', fontSize: 13,
                }}
              />
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 12 }}>
              <button
                onClick={handlePostNow}
                disabled={selectedVariant === null || posting || approving}
                style={{
                  background: selectedVariant !== null ? '#dc2626' : '#333',
                  color: '#fff', border: 'none', borderRadius: 6,
                  padding: '10px 24px', fontSize: 14, fontWeight: 600,
                  cursor: selectedVariant !== null ? 'pointer' : 'not-allowed',
                }}
              >
                {posting ? 'Posting...' : 'Post Now'}
              </button>
              <button
                onClick={handleApprove}
                disabled={selectedVariant === null || approving || posting}
                style={{
                  background: selectedVariant !== null ? '#059669' : '#333',
                  color: '#fff', border: 'none', borderRadius: 6,
                  padding: '10px 24px', fontSize: 14, fontWeight: 600,
                  cursor: selectedVariant !== null ? 'pointer' : 'not-allowed',
                }}
              >
                {approving ? 'Approving...' : 'Schedule for 8am'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Queue --- */}
      <div>
        <h4 style={{ color: '#e2e8f0', margin: '0 0 10px', fontSize: 15 }}>
          Queue
        </h4>
        {queue.length === 0 ? (
          <div style={{ color: '#666', fontSize: 13, padding: '12px 0' }}>
            No dramaturg scenes queued yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {queue.map(item => (
              <div
                key={item.id}
                style={{
                  background: '#1a1a2e', border: '1px solid #333', borderRadius: 6,
                  padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12,
                  cursor: item.status === 'ready' ? 'pointer' : 'default',
                }}
                onClick={() => item.status === 'ready' && handleReopen(item)}
              >
                <span style={{
                  ...STATUS_STYLES[item.status],
                  padding: '2px 8px', borderRadius: 4, fontSize: 11,
                  fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap',
                }}>
                  {item.status}
                </span>
                <span style={{ color: '#e2e8f0', fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.concept_text?.substring(0, 80)}{(item.concept_text?.length || 0) > 80 ? '...' : ''}
                </span>
                {item.media_format && (
                  <span style={{ color: '#666', fontSize: 11 }}>
                    {item.media_format}
                  </span>
                )}
                {item.scheduled_date && (
                  <span style={{ color: '#666', fontSize: 11, whiteSpace: 'nowrap' }}>
                    {item.scheduled_date}
                  </span>
                )}
                {item.error_message && (
                  <span style={{ color: '#f87171', fontSize: 11 }} title={item.error_message}>
                    err
                  </span>
                )}
                {!['posted', 'expired'].includes(item.status) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Delete this concept and its variants permanently?')) {
                        handleDelete(item.id);
                      }
                    }}
                    style={{
                      background: 'transparent', border: '1px solid #555', borderRadius: 4,
                      color: '#888', fontSize: 11, padding: '2px 8px', cursor: 'pointer',
                    }}
                    title="Permanently delete this item"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DramaturgWorkshop;
